/** @jsx jsx */
import { css, jsx } from '@emotion/react';
import './manage-model.css';
import { Component, Fragment, ReactNode } from 'react';
import { withRouter, RouteComponentProps } from 'react-router';
import { sortByKey, getSensorsForEquipmentType, modelImageSource, ensureArray, isDentalEzUser } from '../../utils';
import { AppState } from '../../app.state';
import { get } from 'lodash';
import { _Model, EquipmentTypeName, InstructionsPage, Model, modelHasBoardVersions, Threshold } from '../../_shared/interfaces/model';
import { deleteModel, updateModel } from '../../_shared/services/manage-models.service';
import FormTitle from '../../_shared/form-title';
import Link from '../../_shared/link';
import { Button } from '../../_shared/button';
import { Modal, Table, Tag } from 'antd';
import SharedStyles from '../../_shared/styles';
import ModelImage from '../../_shared/model-image';
import { ViewInstructionsComponent } from '../../_shared/view-instructions/view-instructions.component';
import { getEquipmentForModel } from '../../_shared/services/manage-model.service';
import colors from '../../_shared/colors';
import { AIcon as Icon } from '../../_shared/icon';
import { SN_TYPE_CODE_ANY, normalizeSnTypeCodes } from '../../_shared/lib/sn-type-codes';

interface IProps extends RouteComponentProps {
  sState: AppState;
  updateModels: () => Promise<void>;
}

interface thresholdRow {
  key: string,
  sensorName: string,
  minorLower?: number;
  majorLower?: number;
  minorUpper?: number;
  majorUpper?: number;
  durationLower?: number;
  durationUpper?: number;
  suffix: string;
};

const COLS = [
  {
    title: 'Sensor ID',
    dataIndex: 'key',
  },
  {
    title: 'Sensor',
    dataIndex: 'sensorName',
  },
  {
    title: 'Units',
    dataIndex: 'suffix',
  },
  {
    title: 'Minor Lower',
    dataIndex: 'minorLower',
    editable: true,
  },
  {
    title: 'Major Lower',
    dataIndex: 'majorLower',
    editable: true,
  },
  {
    title: 'Minor Upper',
    dataIndex: 'minorUpper',
    editable: true,
  },
  {
    title: 'Major Upper',
    dataIndex: 'majorUpper',
    editable: true,
  },
  {
    title: 'Duration (Lower)',
    dataIndex: 'durationLower',
    editable: true,
  },
  {
    title: 'Duration (Upper)',
    dataIndex: 'durationUpper',
    editable: true,
  }
];

const ModelImages: React.FC<{
  model?: Model,
}> = props => {
  const { model } = props;
  const showModelImage: boolean = !!modelImageSource(model);
  if (!model || !showModelImage) return null;
  return (<div className='image-column'>
    <div className='image-block'>
      <div className='image-frame'>
        <ModelImage css={css('height: 100%; width: 100%;')} model={model} imageName='' />
      </div>
      <div className='image-caption'>Model Image</div>
    </div>
    {!!get(model, 'images.chairMount0') && <div className='image-block'>
      <div className='image-frame'>
        <ModelImage css={css('height: 100%; width: 100%;')} model={model} imageName='chairMount0' />
      </div>
      <div className='image-caption'>Chair Mount</div>
    </div>}
  </div>);
};

const EmptyValue = <span className='info-empty'>&mdash;</span>;

const VersionTags: React.FC<{
  values?: string | string[],
}> = props => {
  const values = ensureArray(props.values || '').filter(v => `${v}`.length > 0);
  if (!values.length) return EmptyValue;
  return (
    <div className='info-tags'>
      {values.map((v, i) => <Tag key={`${v}-${i}`}>{`${v}`}</Tag>)}
    </div>
  );
};

// AER-1795: a model without codes fails every serial check, so say so rather than show a dash.
const SnTypeCodeTags: React.FC<{
  values?: string[],
}> = props => {
  const codes = normalizeSnTypeCodes(props.values);
  if (!codes.length) {
    return <span className='info-empty'>Not set: serial numbers for this model are rejected</span>;
  }
  return (
    <div className='info-tags'>
      {codes.map(c => <Tag key={c}>{c === SN_TYPE_CODE_ANY ? 'Any serial (*)' : c}</Tag>)}
    </div>
  );
};

interface InfoRowDef {
  label: string;
  value: ReactNode;
}

const InfoPanel: React.FC<{
  model?: Model,
  sState: AppState,
}> = props => {
  const { model } = props;
  if (!model) {
    return (
      <div className='info-panel panel'>
        <div className='panel-title'>Model Info</div>
        <div className='info-empty'>No model selected</div>
      </div>
    );
  }

  const typeName = EquipmentTypeName(model.type);
  const showHeads = model.type.includes('compressor');
  // AER-1713: 5400 and CRK store the same control-board values as the 6400, so
  // surface the firmware/hardware rows for all three.
  const showHardware = modelHasBoardVersions(model);
  const isDez = isDentalEzUser(props.sState);
  const displayModelId = isDez ? get(model, 'id', '') : get(model, 'displayName', '');
  const modelNumber = get(model, 'modelNumber', '');
  const description = get(model, 'description', '');

  const rows: InfoRowDef[] = [
    { label: 'Model ID', value: displayModelId || EmptyValue },
    ...(isDez ? [{ label: 'Display Name/ID', value: get(model, 'displayName', '') || EmptyValue }] : []),
    ...(modelNumber ? [{ label: 'Model Number', value: modelNumber }] : []),
    { label: 'Manufacturer', value: get(model, 'manufacturer', '') || EmptyValue },
    ...(model.family ? [{ label: 'Family', value: get(model, 'family', '') }] : []),
    { label: 'Type', value: typeName || EmptyValue },
    // AER-1795: what Manufacturing and Edit Equipment accept for this model's serials.
    ...(isDez ? [{ label: 'Serial Number Type Codes', value: <SnTypeCodeTags values={model.snTypeCodes} /> }] : []),
    ...(showHeads ? [{ label: 'Heads', value: model.heads || EmptyValue }] : []),
    ...(showHardware ? [
      { label: 'Control Board Firmware', value: <VersionTags values={get(model, 'control_board_fw')} /> },
      { label: 'Control Board Hardware', value: <VersionTags values={get(model, 'control_board_hw')} /> },
      { label: 'Daughterboard Firmware', value: <VersionTags values={get(model, 'fw_version')} /> },
    ] : []),
  ];

  return (
    <div className='info-panel panel'>
      <div className='panel-title'>Model Info</div>
      <div className='info-body'>
        <ModelImages model={model} />
        <div className='info-main'>
          <div className='info-grid'>
            {rows.map(r => (
              <Fragment key={r.label}>
                <div className='info-label'>{r.label}</div>
                <div className='info-value'>{r.value}</div>
              </Fragment>
            ))}
          </div>
          {!!description && <div className='info-description'>
            <div className='info-label'>Description</div>
            <div className='info-description-text'>{description}</div>
          </div>}
        </div>
      </div>
    </div>
  );
};

const InstructionsRow: React.FC<{
  page: InstructionsPage,
  onClick: () => void;
}> = props => {
  const { page, onClick } = props;
  const dType = page.body && page.body.length > 0 ? "check-circle" : "exclamation-circle";
  const iType = page.images && page.images.length > 0 ? "check-circle" : "exclamation-circle";
  return (<tr className='instructions-row' >
    <td className='instructions-page-title'>{page.title}</td>
    <td className='instructions-page-check'><Icon theme='filled' type={dType} /> </td>
    <td className='instructions-page-check'><Icon theme='filled' type={iType} /> </td>
    <td className='instructions-page-check'><Icon theme='outlined' type='edit' onClick={onClick} style={{ color: colors.highlight }} /> </td>
  </tr>)
};

const InstructionsPanel: React.FC<{
  model?: Model,
  previewInstructions: () => void,
  goToPage: (page: number) => void,
}> = props => {
  const { model, previewInstructions, goToPage } = props;

  if (!model) return null;
  const hasInstructions = !!model.instructions && model.instructions.length === 4;
  return (
    <div className='instructions-panel panel'>
      <div className='panel-head'>
        <div className='panel-title'>Installation Instructions</div>
        {hasInstructions
          ? <Button outline title='Preview Instructions' onClick={previewInstructions} />
          : <Button outline title='Configure Instructions' onClick={() => goToPage(0)} />}
      </div>
      {hasInstructions && <table className='instructions-tbl'>
        <thead>
          <tr>
            <th>Steps</th>
            <th style={{ textAlign: 'center' }}>Description</th>
            <th style={{ textAlign: 'center' }}>Image(s)</th>
            <th></th>
          </tr>
        </thead>
        <tbody>
          {(model.instructions as InstructionsPage[]).map((p, i) => <InstructionsRow key={i} page={p} onClick={() => goToPage(i)} />)}
        </tbody>
      </table>}
    </div>
  )
};

const ThresholdsPanel: React.FC<{
  loading: boolean,
  thresholdData: thresholdRow[],
}> = props => {
  const { loading, thresholdData } = props;
  return (
    <div className='thresholds-panel panel'>
      <div className='panel-title'>Alert Thresholds</div>
      <Table
        rowKey={(t: thresholdRow) => t.key}
        size='small'
        dataSource={thresholdData}
        columns={COLS}
        loading={loading}
        pagination={false}
        rowClassName={(_, index) => `tbl-list-row-${index % 2}`}
      />
    </div>
  )
};

export class _ManageModelComponent extends Component<IProps> {
  state = {
    show: true,
    loading: true,
    isEdit: false,
    error: undefined,
    success: undefined,

    model: undefined as Model | undefined,
    thresholdData: [] as thresholdRow[],
    instructionsVisible: false,
    deleting: false,
  };

  componentDidMount = async () => {
    const { history } = this.props;
    const model = get(history, 'location.state.model');

    this.setState(
      {
        model,
      },
      this.retrieveData
    );
  };
  findModelInSState = () => {
    const { sState } = this.props;
    const modelId = get(this.state, 'model.id');
    const models = get(sState, 'dash.models', []) as Model[];
    return new _Model(models.find(m => m.id === modelId) as Model);
  };
  retrieveData = async () => {
    try {
      this.setState({
        loading: true,
        deleting: true
      });

      await this.props.updateModels();

      const { sState } = this.props;
      const model = this.findModelInSState();

      const allSensors = getSensorsForEquipmentType(model.type, sState);

      const thresholdData = allSensors
        .map(sensor => {
          const sensorId = sensor.id;
          const thresh = get(model.thresholds, sensorId, undefined) as Threshold | undefined;
          const row = {
            key: sensorId,
            sensorName: get(sensor, 'name', ''),
            suffix: get(sensor, 'suffix', ''),
          } as thresholdRow;

          if (thresh) {
            row.majorLower = thresh.majorLower;
            row.minorLower = thresh.minorLower;
            row.minorUpper = thresh.minorUpper;
            row.majorUpper = thresh.majorUpper;
            row.durationLower = thresh.durationLower;
            row.durationUpper = thresh.durationUpper;
          }

          return row;
        })
        .sort((a, b) => sortByKey(a, b, 'id'));

      this.setState({
        show: true,
        loading: false,
        deleting: false,
        thresholdData,
        model,
      });
    } catch (err) {
      console.error(err);
      this.setState({
        error: err.message,
        loading: false,
        deleting: false,
      });
    }
  };

  showDeleteModal = async () => {
    if (!this.state || !this.state.model) return;

    const cancel = () => {
      Modal.destroyAll();
      this.setState({ loading: false, deleting: false });
    }

    const ok = async () => {
      if (!this.state.model) {
        cancel();
        return;
      }
      try {
        this.setState({ loading: true });
        await deleteModel(this.state.model)
        this.back();
      } catch (err) {
        console.error(err);
        Modal.destroyAll();
        this.setState({
          error: err.message,
          loading: false,
        });
      }
    }

    try {
      this.setState({ loading: true, deleting: true });
      const equipment = await getEquipmentForModel(this.state.model.id);
      if (equipment.length > 0) {
        Modal.confirm({
          title: 'Cannot Delete Model',
          content: `This model is currently in use by ${equipment.length} equipments.`,
          okButtonProps: { disabled: true },
          okType: 'danger',
          onCancel: cancel,
        });
      } else {
        Modal.confirm({
          title: 'Delete Model?',
          content: 'This cannot be undone.',
          okText: 'Delete',
          onOk: ok,
          onCancel: cancel,
          okType: 'danger'
        });
      }
    } catch (err) {
      console.error(err);
      this.setState({
        error: err.message,
        loading: false,
        deleting: false,
      });
    }
  };

  editModel = (model: Model | undefined, goToPage?: number) => {
    this.props.history.push('/dashboard/editModel', {
      model,
      backText: `< Back to Model Details`,
      editingModel: true,
      goToPage
    });
  };

  changeModel = async (d = {}) => {
    const model = this.findModelInSState();
    const newmodel = { ...model, ...d };
    await updateModel(newmodel);
    this.retrieveData();
  };
  // AER-1699: this used to be history.goBack(), which needed two clicks after a
  // save. Saving in Edit Model does history.replace('/dashboard/manageModel')
  // (edit-model.component.tsx) over the /dashboard/editModel entry this screen
  // pushed, so View Model -> Edit -> Save leaves two consecutive
  // /dashboard/manageModel entries on the stack. goBack() then pops onto the
  // *other* View Model entry: same path, so React re-renders this component
  // instead of remounting it and the screen is byte-identical — the first click
  // looks dead and only the second reaches the list. The same stale-entry pop
  // also happened after a successful delete. Go straight to the list this link
  // names; it is the only screen that ever opens View Model, so that is always
  // the right destination regardless of what is on the history stack.
  back = () => {
    this.props.history.push('/dashboard/manageModels');
  };

  render() {
    const { thresholdData, loading, model, deleting } = this.state;

    const isAerasOne: boolean = get(model, 'type', '').includes('aerasone');

    return (
      <div className='manage-model-container'>
        <div css={css(SharedStyles.row, 'margin-bottom: 10px; justify-content: space-between; align-items: flex-start;')}>
          <Link
            css={css('width: 200px;')}
            onClick={this.back}
          >
            {'< Back to Manage Models'}
          </Link>
          <div className='header-actions'>
            <Button
              title="Delete"
              onClick={this.showDeleteModal}
              disabled={deleting}
              outline
            />
            <Button
              loading={loading}
              title="Refresh"
              onClick={this.retrieveData}
              outline
            />
            <Button
              title="Edit"
              onClick={() => this.editModel(this.state.model)}
            />
          </div>
        </div>
        <div className='model-header'>
          <FormTitle size='20px' css={css('margin: 0;')}>
            {get(model, 'name')}
          </FormTitle>
          {!!model && <Tag className='model-type-tag' color={colors.highlight}>{EquipmentTypeName(model.type)}</Tag>}
        </div>
        <div className='panels-stack'>
          <InfoPanel model={model} sState={this.props.sState} />
          {isAerasOne && <InstructionsPanel
            model={model}
            previewInstructions={() => this.setState({ instructionsVisible: true })}
            goToPage={(page: number) => this.editModel(model, page)} />}
          {isAerasOne && <ThresholdsPanel loading={loading} thresholdData={thresholdData} />}
        </div>

        {this.state.model && this.state.model.instructions && <ViewInstructionsComponent
          instructions={this.state.model!.instructions}
          visible={this.state.instructionsVisible}
          onClose={() => this.setState({ instructionsVisible: false })}
        />}

      </div>
    );
  }
}

//@ts-ignore
export const ManageModelComponent = withRouter(_ManageModelComponent);
